import * as Sentry from '@sentry/react';
import {
	createRandomUuid,
	createTraceContext,
	runIdFromPath,
	toRequestPath,
	withAiServerHeaders,
	type ClientIdentity,
} from './ai-request-trace';

/** One request to the AI chat server, as support needs it to find the server trace and logs. */
export type AiRequestRecord = {
	method: string;
	/** Path without query string or fragment. */
	path: string;
	traceId: string;
	requestId?: string;
	runId?: string;
	/** Absent when the request failed without a response. */
	status?: number;
	failed: boolean;
};

export const CLIENT_IDENTITY: ClientIdentity = {
	sessionId: createRandomUuid(),
	version: process.env.VERSION,
};

let lastRequest: AiRequestRecord | null = null;
let lastFailedRequest: AiRequestRecord | null = null;

export const getAiRequestDebugState = () => ({ lastRequest, lastFailedRequest });

const requestUrl = (input: RequestInfo | URL) => {
	if (typeof input === 'string') return input;
	return input instanceof URL ? input.href : input.url;
};

const isAbortError = (error: unknown) => error instanceof Error && error.name === 'AbortError';

function report(record: AiRequestRecord) {
	lastRequest = record;
	if (record.failed) lastFailedRequest = record;
	Sentry.addBreadcrumb({
		category: 'ai.chat',
		type: 'http',
		level: record.failed ? 'error' : 'info',
		data: {
			method: record.method,
			path: record.path,
			status: record.status,
			traceId: record.traceId,
			requestId: record.requestId,
			runId: record.runId,
		},
	});
	if (record.failed) setAiErrorTags(record);
}

function setAiErrorTags({ runId, traceId }: { runId?: string; traceId?: string }) {
	if (traceId) Sentry.setTag('ai.trace_id', traceId);
	if (runId) Sentry.setTag('ai.run_id', runId);
}

/** Tags Sentry with a run that ended in error, so later events link to its server trace. */
export function reportAiRunError({ runId, traceId }: { runId?: string; traceId?: string }) {
	setAiErrorTags({ runId, traceId });
	Sentry.addBreadcrumb({
		category: 'ai.chat',
		level: 'error',
		message: 'AI run ended in error',
		data: { runId, traceId },
	});
}

/**
 * `fetch` for every admin request to the AI chat server. It adds a fresh trace context and the client
 * identity headers, keeps the caller's headers, and records the request for "Copy debug info" and Sentry.
 */
export const aiServerFetch: typeof globalThis.fetch = async (input, init) => {
	const trace = createTraceContext();
	const path = toRequestPath(requestUrl(input));
	const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
	const callerHeaders = init?.headers ?? (input instanceof Request ? input.headers : undefined);
	const base = { method, path, runId: runIdFromPath(path) };
	let response: Response;
	try {
		response = await globalThis.fetch(input, {
			...init,
			headers: withAiServerHeaders(callerHeaders, trace, CLIENT_IDENTITY),
		});
	} catch (error) {
		if (!isAbortError(error)) report({ ...base, traceId: trace.traceId, failed: true });
		throw error;
	}
	report({
		...base,
		traceId: response.headers.get('x-trace-id') ?? trace.traceId,
		requestId: response.headers.get('x-request-id') ?? undefined,
		status: response.status,
		failed: !response.ok,
	});
	return response;
};

/** Trace and client identity headers for AI chat server requests that do not go through `fetch`. */
export const createAiServerHeaders = (): Record<string, string> =>
	Object.fromEntries(withAiServerHeaders(undefined, createTraceContext(), CLIENT_IDENTITY).entries());
